import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/Navbar.css";
import { Button } from "primereact/button";
import PolygonExpansionControl from "./PolygonExpansionControl";
import ActiveFilters, { countActiveFilters } from "./components/ActiveFilters.jsx";
import { DECIMAL_FILTER_KEYS, FILTER_INPUT_LABELS, validateFilterDraft } from "./utils/filterDraft";
import logo from "../../assets/logo-cotify-horizontal-negro.png";
import {
  blockInvalidNumberKeys,
  toNonNegativeDecimalOrNull,
  toNonNegativeIntOrNull,
} from "../../utils/nonNegativeNumberInput.js";

const MIN_RADIUS = 200;
const MAX_RADIUS = 1500;
const DEFAULT_RADIUS = 500;
const RADIUS_DEBOUNCE_MS = 700;

// Stable component identity keeps focus and selection while typing.
const FilterNumberInput = ({
  name,
  value,
  placeholder,
  onChange,
  className = "styled-input",
  allowDecimal = false,
}) => (
  <input
    id={`filter-${name}`}
    name={name}
    type={allowDecimal ? "text" : "number"}
    min={allowDecimal ? undefined : 0}
    step={allowDecimal ? undefined : 1}
    value={value ?? ""}
    aria-label={FILTER_INPUT_LABELS[name]}
    onChange={event => onChange(name, event.target.value)}
    onKeyDown={(e) => blockInvalidNumberKeys(e, { allowDecimal })}
    placeholder={placeholder}
    className={className}
    inputMode={allowDecimal ? "decimal" : "numeric"}
  />
);

const Navbar = ({
  filters,
  setFilters,
  // Búsqueda por zona
  searchMode = 'radius',
  onSearchModeChange,
  polygonsCount = 0,
  polygonExpansion = 0,
  onPolygonExpansionChange,
  filtersRequest = 0,
  suggestionPulse = 0,
}) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filtersJustUpdated, setFiltersJustUpdated] = useState(false);
  const [draftChanges, setDraftChanges] = useState({});
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const activeCount = countActiveFilters(filters);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const sync = () => {
      document.documentElement.style.setProperty(
        "--home-nav-bottom",
        `${Math.round(el.getBoundingClientRect().bottom)}px`,
      );
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    window.addEventListener("resize", sync);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, []);

  const handleCreateNewReport = () => {
    navigate("/property-form");
  };

  // --- Radius con debounce ------------------------------------------------
  // El slider actualiza inmediatamente un estado local (feedback visual),
  // y pushea al store global recién después de RADIUS_DEBOUNCE_MS para
  // evitar una cascada de requests mientras se arrastra.
  const globalRadius = Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, Number(filters.radius ?? DEFAULT_RADIUS)));
  const [localRadius, setLocalRadius] = useState(globalRadius);
  const debounceRef = useRef(null);

  // Si `filters.radius` cambia por fuera (reset, reanudar sesión), sincronizamos.
  useEffect(() => {
    setLocalRadius(globalRadius);
  }, [globalRadius]);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const getRadiusFillPercent = (value) => {
    const pct = ((Number(value) - MIN_RADIUS) / (MAX_RADIUS - MIN_RADIUS)) * 100;
    return `${Math.min(100, Math.max(0, pct))}%`;
  };

  const handleRadiusChange = (value) => {
    const n = Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, Number(value)));
    setLocalRadius(n);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, radius: n }));
    }, RADIUS_DEBOUNCE_MS);
  };

  // Qué modos están habilitados según datos disponibles.
  const hasCenter = Boolean(filters.center);
  const canUsePolygons = polygonsCount > 0;
  const canUseRadius = hasCenter;

  const draftFilters = { ...filters, ...draftChanges };
  const hasPendingChanges = Object.keys(draftChanges).some(key => draftChanges[key] !== filters[key]);
  const draftError = validateFilterDraft(draftFilters);

  const closeFilters = () => {
    setDraftChanges({});
    setShowAdvancedFilters(false);
  };
  useEffect(() => {
    if (!filtersRequest) return;
    setDraftChanges({});
    setShowAdvancedFilters(true);
  }, [filtersRequest]);

  useEffect(() => {
    if (!suggestionPulse) return undefined;
    setFiltersJustUpdated(false);
    const frame = requestAnimationFrame(() => setFiltersJustUpdated(true));
    const timer = setTimeout(() => setFiltersJustUpdated(false), 900);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [suggestionPulse]);

  useEffect(() => {
    if (!showAdvancedFilters) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") closeFilters();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAdvancedFilters]);
  const toggleFilters = () => {
    if (showAdvancedFilters) closeFilters();
    else {
      setDraftChanges({});
      setShowAdvancedFilters(true);
    }
  };
  const handleFilterChange = (name, value) => {
    setDraftChanges(previous => name === "currency" ? {
      ...previous,
      currency: value,
      minPrice: value ? 0 : null,
      maxPrice: value === "USD" ? 1000000 : value ? 300000000 : null,
    } : { ...previous, [name]: value });
  };
  const handleNumericFilterChange = (name, raw) => {
    const parsed = DECIMAL_FILTER_KEYS.has(name)
      ? toNonNegativeDecimalOrNull(raw)
      : toNonNegativeIntOrNull(raw);
    if (parsed !== undefined) handleFilterChange(name, parsed);
  };
  const handleApplyFilters = (event) => {
    event.preventDefault();
    if (draftError) return;
    if (hasPendingChanges) {
      setFilters((current) => {
        const next = { ...current, ...draftChanges };
        // Normalizar m² a number (el draft puede traer strings mientras se escribe).
        for (const key of DECIMAL_FILTER_KEYS) {
          if (next[key] != null && next[key] !== "") {
            const n = Number(next[key]);
            next[key] = Number.isFinite(n) ? n : null;
          }
        }
        return next;
      });
    }
    closeFilters();
  };

  return (
    <header className="navbar" ref={headerRef}>
      <div className="navbar-brand">
        <div className="navbar-left">
          <Button
            icon="pi pi-arrow-left"
            className="p-button-text"
            aria-label="Volver al panel"
            onClick={() => (window.location.href = process.env.REACT_APP_PANEL_URL)}
          />
        </div>
        <div className="logo">
          <img src={logo} alt="Cotify Logo" />
        </div>
      </div>

      <div className="navbar-tools">
        <div className="zone-control-wrapper">
          <div
            className="search-mode-toggle"
            role="group"
            aria-label="Modo de búsqueda de zona"
          >
            <button
              type="button"
              className={`smt-btn ${searchMode === 'radius' ? 'active' : ''}`}
              onClick={() => onSearchModeChange?.('radius')}
              disabled={!canUseRadius}
              title={!canUseRadius ? 'Sin ubicación guardada en la sesión' : 'Buscar por radio'}
            >
              <i className="pi pi-circle" /> Radio
            </button>
            <button
              type="button"
              className={`smt-btn ${searchMode === 'polygons' ? 'active' : ''}`}
              onClick={() => onSearchModeChange?.('polygons')}
              disabled={!canUsePolygons}
              title={!canUsePolygons ? 'No dibujaste zonas en el wizard' : 'Buscar por zonas dibujadas'}
            >
              <i className="pi pi-pencil" /> Zonas
              {polygonsCount > 0 && <span className="smt-badge">{polygonsCount}</span>}
            </button>
          </div>

          {searchMode === 'radius' ? (
            <div className="radius-controls">
              <input
                type="range"
                className="radius-slider"
                aria-label="Radio de búsqueda"
                min={MIN_RADIUS}
                max={MAX_RADIUS}
                step={50}
                value={localRadius}
                onChange={(e) => handleRadiusChange(e.target.value)}
                style={{
                  '--radius-fill': getRadiusFillPercent(localRadius),
                }}
              />
              <output className="radius-controls__value">{localRadius} m</output>
            </div>
          ) : (
            <PolygonExpansionControl
              value={polygonExpansion}
              onChange={onPolygonExpansionChange}
              disabled={!canUsePolygons}
            />
          )}
        </div>

        <div className="main-filters">
          <div className={`nav-filters-wrap${filtersJustUpdated ? " nav-filters-wrap--updated" : ""}`}>
            <Button
              icon={showAdvancedFilters ? "pi pi-times" : "pi pi-filter"}
              className={`nav-btn-filters${showAdvancedFilters ? ' nav-btn-filters--active' : ''}`}
              onClick={toggleFilters}
              aria-expanded={showAdvancedFilters}
              label="Filtros"
              aria-label={activeCount > 0 ? `Filtros, ${activeCount} aplicados` : "Filtros"}
            />
            {activeCount > 0 && <span className="nav-filters-count">{activeCount}</span>}
          </div>
          <Button
            icon="pi pi-plus"
            label="Nuevo informe"
            aria-label="Crear nuevo informe"
            className="nav-btn-create"
            onClick={handleCreateNewReport}
          />
        </div>
      </div>

      <div className="navbar-center">

          <button
            type="button"
            className={`filter-drawer-backdrop${showAdvancedFilters ? " is-open" : ""}`}
            aria-label="Cerrar filtros sin aplicar"
            onClick={closeFilters}
            tabIndex={showAdvancedFilters ? 0 : -1}
          />
          <div
            className={`filter-drawer advanced-filters-panel${showAdvancedFilters ? " is-open" : ""}`}
            role="dialog"
            aria-modal={showAdvancedFilters}
            aria-hidden={!showAdvancedFilters}
            inert={showAdvancedFilters ? undefined : true}
            aria-label="Filtros de búsqueda"
          >
          <div className="filter-editor-heading">
            <div><h2>Filtros de búsqueda</h2><p>Completá los valores y aplicalos cuando estés listo.</p></div>
            <button type="button" className="filter-editor-close" onClick={closeFilters} aria-label="Cerrar filtros sin aplicar"><i className="pi pi-times" aria-hidden="true" /></button>
          </div>
          <form id="search-filters-form" className="filter-drawer__form" onSubmit={handleApplyFilters} noValidate>
            <div className="filter-editor-body">
            <ActiveFilters
              filters={filters}
              onRemoveFilter={(filterKey) => {
                const isBoolKey = ['hasPool', 'hasGrill', 'hasGarden', 'hasTerrace', 'hasBalcony', 'garage'].includes(filterKey);
                const cleared = isBoolKey ? false : null;
                setFilters(prev => ({ ...prev, [filterKey]: cleared }));
                setDraftChanges(prev => {
                  if (!(filterKey in prev)) return prev;
                  const next = { ...prev };
                  delete next[filterKey];
                  return next;
                });
              }}
            />
            <div className="filters-grid">
              {/* Precio y Moneda */}
              <div className="filter-section filter-section--price">
                <h4 className="filter-section__title">
                  <i className="pi pi-dollar" aria-hidden="true" />
                  Precio
                </h4>
                <div className="price-filter-row">
                  <div className="input-group input-group--price">
                    <select
                      value={draftFilters.currency ?? ""}
                      onChange={(e) => handleFilterChange("currency", e.target.value)}
                      className="styled-select styled-select--currency"
                      aria-label="Moneda"
                    >
                      <option value="">Todas</option>
                      <option value="USD">USD</option>
                      <option value="$">ARS</option>
                    </select>
                    <FilterNumberInput onChange={handleNumericFilterChange}
                      name="minPrice"
                      value={draftFilters.minPrice}
                      placeholder="Desde"
                    />
                    <span className="separator">–</span>
                    <FilterNumberInput onChange={handleNumericFilterChange}
                      name="maxPrice"
                      value={draftFilters.maxPrice}
                      placeholder="Hasta"
                    />
                  </div>
                </div>
              </div>

              {/* Características + Antigüedad */}
              <div className="filter-section filter-section--features">
                <h4 className="filter-section__title">
                  <i className="pi pi-th-large" aria-hidden="true" />
                  Características
                </h4>
                <div className="filter-fields-stack">
                  <div className="filter-field filter-field--row">
                    <label>Ambientes</label>
                    <div className="input-group">
                      <FilterNumberInput onChange={handleNumericFilterChange} name="minRooms" value={draftFilters.minRooms} placeholder="Mín." />
                      <span className="separator">–</span>
                      <FilterNumberInput onChange={handleNumericFilterChange} name="maxRooms" value={draftFilters.maxRooms} placeholder="Máx." />
                    </div>
                  </div>
                  <div className="filter-field filter-field--row">
                    <label>Dormitorios</label>
                    <div className="input-group">
                      <FilterNumberInput onChange={handleNumericFilterChange} name="minBedrooms" value={draftFilters.minBedrooms} placeholder="Mín." />
                      <span className="separator">–</span>
                      <FilterNumberInput onChange={handleNumericFilterChange} name="maxBedrooms" value={draftFilters.maxBedrooms} placeholder="Máx." />
                    </div>
                  </div>
                  <div className="filter-field filter-field--row">
                    <label>Baños</label>
                    <div className="input-group">
                      <FilterNumberInput onChange={handleNumericFilterChange} name="minBathrooms" value={draftFilters.minBathrooms} placeholder="Mín." />
                      <span className="separator">–</span>
                      <FilterNumberInput onChange={handleNumericFilterChange} name="maxBathrooms" value={draftFilters.maxBathrooms} placeholder="Máx." />
                    </div>
                  </div>
                  <div className="filter-field filter-field--row">
                    <label>Antigüedad</label>
                    <div className="input-group">
                      <FilterNumberInput onChange={handleNumericFilterChange} name="minAntiquity" value={draftFilters.minAntiquity} placeholder="Mín." />
                      <span className="separator">–</span>
                      <FilterNumberInput onChange={handleNumericFilterChange} name="maxAntiquity" value={draftFilters.maxAntiquity} placeholder="Máx." />
                    </div>
                  </div>
                </div>
              </div>

              {/* Dimensiones */}
              <div className="filter-section filter-section--dims">
                <h4 className="filter-section__title">
                  <i className="pi pi-arrows-alt" aria-hidden="true" />
                  Dimensiones
                </h4>
                <div className="filter-fields-stack">
                  <div className="filter-field filter-field--row">
                    <label>m² cubiertos</label>
                    <div className="input-group">
                      <FilterNumberInput onChange={handleNumericFilterChange}
                        name="minSquareMetersCovered"
                        value={draftFilters.minSquareMetersCovered}
                        placeholder="Mín."
                        allowDecimal
                      />
                      <span className="separator">–</span>
                      <FilterNumberInput onChange={handleNumericFilterChange}
                        name="maxSquareMetersCovered"
                        value={draftFilters.maxSquareMetersCovered}
                        placeholder="Máx."
                        allowDecimal
                      />
                    </div>
                  </div>
                  <div className="filter-field filter-field--row">
                    <label>m² totales</label>
                    <div className="input-group">
                      <FilterNumberInput onChange={handleNumericFilterChange}
                        name="minSquareMetersTotal"
                        value={draftFilters.minSquareMetersTotal}
                        placeholder="Mín."
                        allowDecimal
                      />
                      <span className="separator">–</span>
                      <FilterNumberInput onChange={handleNumericFilterChange}
                        name="maxSquareMetersTotal"
                        value={draftFilters.maxSquareMetersTotal}
                        placeholder="Máx."
                        allowDecimal
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Comodidades */}
              <div className="filter-section filter-section--amenities">
                <h4 className="filter-section__title">
                  <i className="pi pi-sun" aria-hidden="true" />
                  Comodidades
                </h4>
                <div className="filter-amenities">
                  {[
                    { label: "Pileta", field: "hasPool", icon: "🏊" },
                    { label: "Parrilla", field: "hasGrill", icon: "🔥" },
                    { label: "Jardín", field: "hasGarden", icon: "🌿" },
                    { label: "Terraza", field: "hasTerrace", icon: "🌅" },
                    { label: "Balcón", field: "hasBalcony", icon: "🪟" },
                  ].map(({ label, field, icon }) => (
                    <button
                      key={field}
                      type="button"
                      className={`filter-amenity-chip${draftFilters[field] ? " filter-amenity-chip--active" : ""}`}
                      onClick={() => handleFilterChange(field, !draftFilters[field])}
                      aria-pressed={Boolean(draftFilters[field])}
                    >
                      <span className="filter-amenity-chip__icon" aria-hidden="true">{icon}</span>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="filter-parking-row">
                  <span id="filter-parking-label">Cocheras</span>
                  <div className="filter-stepper" role="group" aria-labelledby="filter-parking-label">
                    <button
                      type="button"
                      className="filter-stepper__btn"
                      aria-label="Menos cocheras"
                      disabled={!draftFilters.minParking}
                      onClick={() => handleFilterChange("minParking", Number(draftFilters.minParking) - 1 || null)}
                    />
                    <span className="filter-stepper__value">{draftFilters.minParking || 0}</span>
                    <button
                      type="button"
                      className="filter-stepper__btn filter-stepper__btn--plus"
                      aria-label="Más cocheras"
                      onClick={() => handleFilterChange("minParking", Number(draftFilters.minParking || 0) + 1)}
                    />
                  </div>
                </div>
              </div>
            </div>
            </div>
            <div className="filter-editor-footer">
              <div className="filter-editor-status" role="status">
                {draftError ? <span className="filter-editor-error">{draftError}</span>
                  : hasPendingChanges ? "Tenés cambios sin aplicar." : "Estos son los filtros de la búsqueda actual."}
              </div>
              <div className="filter-editor-actions">
                <button type="button" className="filter-editor-cancel" onClick={closeFilters}>Cancelar</button>
                <button type="submit" className="filter-editor-apply" disabled={Boolean(draftError)}>Aplicar filtros</button>
              </div>
            </div>
          </form>
          </div>
      </div>
    </header>
  );
};

export default Navbar;